import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { icons } from "../../../icons";
import { HeadLine3, BodyDefault, BodySmall } from "../../typography/styled";
import Button from "../../button";
import { clusterSettingsService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import { OperationDetail as OperationDetailType, OperationStep } from "../../../pages/settings/clusters/types";
import { formatDate } from "../../../utils/date-formatter";
import {
  Container,
  HeadWrapper,
  BodyModal,
  Icon,
} from "../create-node-pool/styles";

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  border: ${({ theme }) => `1px solid ${theme.palette[20]}`};
  border-radius: 4px;
  gap: 4px;
`;

const StepName = styled(BodyDefault)`
  font-weight: 600;
`;

const StepStatus = styled.span<{ status: string }>`
  color: ${({ status }) =>
    status === "completed"
      ? "#229B61"
      : status === "error"
      ? "#E85048"
      : status === "running"
      ? "#4671F6"
      : "#888"};
  font-weight: 500;
`;

type ModalProps = {
  closeModalCancel: () => void;
  closeModal: (data?: any) => void;
  dataToEdit?: any;
};

const OperationDetailModal: FC<ModalProps> = ({ closeModalCancel, dataToEdit }) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [detail, setDetail] = useState<OperationDetailType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dataToEdit?.id) return;
    clusterSettingsService
      .getOperation(dataToEdit.id)
      .then((res) => setDetail(res.data))
      .catch((err) => {
        viewAlert({
          severity: "error",
          message: err?.response?.data?.detail || t("ERROR_OCCURRED"),
        });
      })
      .finally(() => setLoading(false));
  }, [dataToEdit]);

  const steps = detail?.progress
    ? Object.entries(detail.progress).map(([key, step]) => ({
        key,
        ...step,
      }))
    : [];

  return (
    <Container style={{ width: "550px" }}>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: "10px", marginTop: "10px" }}>
          {detail?.name || dataToEdit?.name || t("OPERATION_DETAILS")}
        </HeadLine3>
        <Icon onClick={closeModalCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        {loading ? (
          <BodyDefault>{t("LOADING")}...</BodyDefault>
        ) : detail ? (
          <>
            <BodyDefault>
              <strong>{t("STATUS")}:</strong> {detail.status?.toUpperCase()}
            </BodyDefault>
            {detail.description && (
              <BodyDefault>
                <strong>{t("DESCRIPTION")}:</strong> {detail.description}
              </BodyDefault>
            )}
            <BodyDefault>
              <strong>{t("CREATED_AT")}:</strong> {formatDate(detail.created_at)}
            </BodyDefault>
            {detail.started_at && (
              <BodyDefault>
                <strong>{t("STARTED_AT")}:</strong> {formatDate(detail.started_at)}
              </BodyDefault>
            )}
            {detail.completed_at && (
              <BodyDefault>
                <strong>{t("COMPLETED_AT")}:</strong> {formatDate(detail.completed_at)}
              </BodyDefault>
            )}
            {steps.length > 0 && (
              <>
                <BodyDefault style={{ fontWeight: 600, marginTop: "8px" }}>
                  {t("STEPS")}:
                </BodyDefault>
                {steps.map((step) => (
                  <StepItem key={step.key}>
                    <StepName>{step.display || step.name || step.key}</StepName>
                    <BodySmall>
                      <StepStatus status={step.status}>{step.status}</StepStatus>
                      {step.action_msg && ` — ${step.action_msg}`}
                    </BodySmall>
                    {step.error_msg && (
                      <BodySmall style={{ color: "#E85048" }}>
                        {step.error_msg}
                      </BodySmall>
                    )}
                  </StepItem>
                ))}
              </>
            )}
          </>
        ) : null}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "6px" }}>
          <Button variant="stroke" size="display" customStyles={{ width: "177px", justifyContent: "center" }} onClick={closeModalCancel} title={t("CLOSE")} />
        </div>
      </BodyModal>
    </Container>
  );
};

export default OperationDetailModal;
